import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Eye, EyeOff, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ResetPasswordFormSchema } from '@/components/forms/schemas/ResetPassowrdFormSchema';
import { FormInput } from '@/components/global/form-inputs/FormInput';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useChangePassword } from '@/hooks/auth/useChangePassword';
import { toast } from '@/hooks/ui/use-toast';
import useMyProfileStore from '@/stores/useMyProfileStore';

export const ResetPasswordForm = () => {
  // state
  const [isReset, setIsReset] = useState<boolean>(false);
  const { myProfile } = useMyProfileStore();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<z.infer<typeof ResetPasswordFormSchema>>({
    resolver: zodResolver(ResetPasswordFormSchema),
    defaultValues: {},
  });

  const newPassword = form.watch('new_password');
  const confirmNewPassword = form.watch('confirm_new_password');

  const passwordRequirements = {
    minLength: newPassword?.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword || ''),
    hasLowercase: /[a-z]/.test(newPassword || ''),
    hasNumber: /[0-9]/.test(newPassword || ''),
    hasSpecialChar: /[^A-Za-z0-9]/.test(newPassword || ''),
    passwordsMatch:
      newPassword === confirmNewPassword && newPassword?.length > 0,
  };

  const allRequirementsMet =
    passwordRequirements.minLength &&
    passwordRequirements.hasUppercase &&
    passwordRequirements.hasLowercase &&
    passwordRequirements.hasNumber &&
    passwordRequirements.hasSpecialChar &&
    passwordRequirements.passwordsMatch;

  // hooks
  const {
    mutate: changePassword,
    error: changePasswordError,
    isError: isChangePasswordError,
    isSuccess: isChangePasswordSuccess,
    isLoading: isChangePasswordLoading,
  } = useChangePassword();

  // event handlers
  const onSubmit = async (values: z.infer<typeof ResetPasswordFormSchema>) => {
    if (!myProfile?.id) {
      toast({
        title: 'Error',
        description: 'User ID not available. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await changePassword({
        userId: myProfile.id,
        oldPassword: values.old_password,
        newPassword: values.new_password,
      });
    } catch (error) {
      console.error('Password change error:', error);
    }
  };

  const handleStartReset = () => {
    setIsReset(true);
  };

  const handleCancelReset = () => {
    setIsReset(false);
  };

  // effects
  useEffect(() => {
    if (isChangePasswordError) {
      toast({
        title: 'Password Change Failed',
        description:
          changePasswordError?.message ||
          'Failed to change password. Please check your old password and try again.',
        variant: 'destructive',
      });
    }
  }, [changePasswordError, isChangePasswordError]);

  useEffect(() => {
    if (isChangePasswordSuccess) {
      toast({
        title: 'Password Changed Successfully',
        description: 'Your password has been updated successfully.',
      });
      form.reset();
      setIsReset(false);
    }
  }, [isChangePasswordSuccess, form]);

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-500" />
      )}
      <span className={met ? 'text-green-600' : 'text-gray-600'}>{text}</span>
    </div>
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col items-start justify-between gap-6 rounded-xl border border-border bg-white p-6 md:flex-row">
          <div className="as-title-03 as-strong text-zinc-700">Password</div>
          {!isReset && (
            <Button type="button" variant="outline" onClick={handleStartReset}>
              Reset password
            </Button>
          )}
          {isReset && (
            <div className="flex w-full flex-col items-end gap-5">
              <div className="grid w-full flex-1 grid-cols-2 items-end gap-6">
                <div className="relative">
                  <FormInput
                    control={form.control}
                    name="old_password"
                    label="Old Password"
                    type={showOldPassword ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showOldPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="grid w-full flex-1 grid-cols-2 items-end gap-6">
                <div className="relative">
                  <FormInput
                    control={form.control}
                    name="new_password"
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>

                <div className="relative">
                  <FormInput
                    control={form.control}
                    name="confirm_new_password"
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {newPassword && (
                <div className="w-full space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-700">
                    Password Requirements:
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <RequirementItem
                      met={passwordRequirements.minLength}
                      text="At least 8 characters"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasUppercase}
                      text="At least one uppercase letter"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasLowercase}
                      text="At least one lowercase letter"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasNumber}
                      text="At least one number"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasSpecialChar}
                      text="At least one special character"
                    />
                    {confirmNewPassword && (
                      <RequirementItem
                        met={passwordRequirements.passwordsMatch}
                        text="Passwords match"
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={handleCancelReset}
                >
                  Cancel
                </Button>
                <Button
                  size={'sm'}
                  disabled={isChangePasswordLoading || !allRequirementsMet}
                >
                  {isChangePasswordLoading && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  Save new password
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
};
